import React, { useState, useEffect } from 'react';
import browserDetection from '../utils/browserDetection';
import './BrowserWarning.css';

const BrowserWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [compatibility, setCompatibility] = useState(null);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed this session
    const dismissed = sessionStorage.getItem('browserWarningDismissed');
    if (dismissed) {
      setIsDismissed(true);
      return;
    }

    // Check browser compatibility
    const compat = browserDetection.checkCompatibility();
    setCompatibility(compat);

    // Show warning if browser is problematic
    if (compat.isProblematic || compat.issues.some((i) => i.severity === 'critical')) {
      setShowWarning(true);
    }
  }, []);

  const handleDismiss = () => {
    sessionStorage.setItem('browserWarningDismissed', 'true');
    setShowWarning(false);
    setIsDismissed(true);
  };

  const handleSwitchBrowser = () => {
    // Open information about recommended browsers
    window.open('https://www.google.com/chrome/', '_blank', 'noopener,noreferrer');
  };

  if (!showWarning || isDismissed) return null;

  return (
    <div className="browser-warning-overlay">
      <div className="browser-warning-modal">
        <div className="warning-header">
          <span className="warning-icon">⚠️</span>
          <h2>Browser Compatibility Issue Detected</h2>
        </div>

        <div className="warning-content">
          {compatibility?.isDuckDuckGo && (
            <div className="browser-specific-message duckduckgo">
              <h3>DuckDuckGo Browser Detected</h3>
              <p>
                We respect your privacy choices! However, DuckDuckGo's enhanced privacy protections
                block the WebSocket connections required for real-time multiplayer games.
              </p>
              <p className="explanation">
                <strong>Why this happens:</strong> DuckDuckGo blocks cross-origin WebSocket
                connections to protect your privacy. While this is great for browsing, it prevents
                the real-time communication needed for multiplayer card games.
              </p>
            </div>
          )}

          {compatibility?.issues.length > 0 && (
            <div className="issues-list">
              <h4>Specific Issues:</h4>
              <ul>
                {compatibility.issues.map((issue, index) => (
                  <li key={index} className={`issue-${issue.severity}`}>
                    <strong>{issue.message}</strong>
                    <br />
                    <span className="issue-detail">{issue.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="recommended-browsers">
            <h4>Recommended Browsers:</h4>
            <div className="browser-grid">
              <div className="browser-option">
                <span className="browser-icon">🌐</span>
                <span>Chrome</span>
              </div>
              <div className="browser-option">
                <span className="browser-icon">🦊</span>
                <span>Firefox</span>
              </div>
              <div className="browser-option">
                <span className="browser-icon">🧭</span>
                <span>Safari</span>
              </div>
              <div className="browser-option">
                <span className="browser-icon">📘</span>
                <span>Edge</span>
              </div>
            </div>
          </div>

          <div className="warning-note">
            <p>
              <strong>Note:</strong> You can still try to play, but you may experience connection
              issues, inability to join rooms, or sudden disconnections.
            </p>
          </div>
        </div>

        <div className="warning-actions">
          <button className="btn-switch-browser" onClick={handleSwitchBrowser}>
            Download Chrome
          </button>
          <button className="btn-proceed-anyway" onClick={handleDismiss}>
            Proceed Anyway (Not Recommended)
          </button>
        </div>

        <div className="warning-footer">
          <small>
            This game requires WebSocket connections for real-time multiplayer functionality.
            Privacy-focused browsers may block these connections.
          </small>
        </div>
      </div>
    </div>
  );
};

export default BrowserWarning;
